import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { resolveHref } from '../utils/resolveHref';
import { useCtaColorVars } from '../hooks/useCtaColorVars';
import { onColorLinksCss } from '../utils/onColorLinks';

// ============================================================================
// Images: each row ships with a hotlink-safe Unsplash stock photo as its
// `defaultValue` so the Better-Built Living page always has editorial visuals
// on a fresh property — before anyone uploads property-specific construction
// shots. The row 1 generator is retained so AI-fill can still swap in a
// property-specific exterior; the editor's media picker can replace any row
// manually.
//
// Editorial image-and-text rows that explain why Z Modular's construction
// method delivers "Better-Built Living":
//
//   Row 1 — Steel vs. wood: structural strength, soundproofing, pest/fire
//           resistance.
//   Row 2 — Off-site modular assembly: factory precision, shorter build
//           times, less jobsite waste.
//
// The visual language mirrors `LifestyleFeatureRows` (alternating light/dark
// bands, image side flips per row) so the page composes with the rest of the
// Z Modular theme. Row 1 renders light (editorial counterpoint) and Row 2
// renders as the dark angled primary-color band — same alternation rhythm as
// the home page's `PropertyOverview → ConvenienceCallout` beat.
// ============================================================================

const row1Def: SlotGroupDefinition = {
  label: 'Row 1 — Quality at the Core',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      // Prefer the property's own featured image — the same single-image
      // field Hero / HeroSolidBG use, so on a live property this reliably
      // shows that community's building exterior. Falls back to a modern
      // residential building Unsplash stock photo for properties without a
      // featured image set.
      defaultField: 'property_featured_image',
      defaultValue:
        'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200&q=80&auto=format&fit=crop',
      generator: {
        prompt:
          'Pick a wide building exterior photo of the property — architectural facade, aerial view, or street-level shot that shows the finished home structure. Skip interiors, unit close-ups, floor-plan diagrams, amenity rooms, pools, and signage-only shots.',
        context:
          'Row 1 of 3 in the Better-Built Living construction section, paired with "Quality at the Core" copy about American-made steel framing, durability, and soundproofing. Half-width editorial image inside a light row; should read as a finished, standing building.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Building exterior at the community',
      generator: {
        prompt:
          'Short descriptive alt text (6–12 words) for the chosen building exterior image. Describe what is visibly in the frame — building type, materials, or vantage — not marketing adjectives.',
        context:
          'Pairs with the Row 1 image above in the Better-Built Living construction section ("Quality at the Core"). Keep it factual and screen-reader friendly.',
      },
    },
    title: {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Quality at the Core',
    },
    body: {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>For us, quality is a foundation, not just a feature. With American-made steel at the heart of our homes, we deliver unmatched strength, durability, and soundproofing. These qualities create solid, quieter spaces where you can thrive.</p>',
    },
    url: {
      label: 'CTA URL',
      type: 'url',
      defaultValue: '',
    },
    label: {
      label: 'CTA Label',
      type: 'text',
      defaultValue: '',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const row2Def: SlotGroupDefinition = {
  label: 'Row 2 — Smarter, More Sustainable Building',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      // Factory / warehouse interior — stands in for the controlled off-site
      // modular manufacturing environment described in the body copy.
      defaultValue:
        'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80&auto=format&fit=crop',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Controlled off-site manufacturing facility',
    },
    title: {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Smarter, More Sustainable Building',
    },
    body: {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>Our off-site construction process values precision, efficiency, and sustainability. By designing and building in a controlled environment, we ensure every component meets rigorous standards. This approach reduces waste, shortens build times, and results in homes that are better for you and the environment.</p>',
    },
    url: {
      label: 'CTA URL',
      type: 'url',
      defaultValue: '',
    },
    label: {
      label: 'CTA Label',
      type: 'text',
      defaultValue: '',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const row3Def: SlotGroupDefinition = {
  label: 'Row 3 — Built to Last: Steel vs. Wood',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      // Steel framing / construction detail — visually makes the "steel vs.
      // wood" argument before the body copy spells it out.
      defaultValue:
        'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1200&q=80&auto=format&fit=crop',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'American-made steel framing',
    },
    title: {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Built to Last: Steel vs. Wood',
    },
    body: {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<ul><li><strong>The Steel Advantage:</strong> Unlike wood, steel resists warping and cracking, offering long-lasting durability and minimal maintenance.</li><li><strong>Enhanced Protection:</strong> Steel is non-combustible and less vulnerable to pests, offering enhanced protection for your home.</li></ul>',
    },
    url: {
      label: 'CTA URL',
      type: 'url',
      defaultValue: '',
    },
    label: {
      label: 'CTA Label',
      type: 'text',
      defaultValue: '',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// ============================================================================
// Reusable row renderer — mirrors LifestyleFeatureRows' alternation strategy
// so stacked bands read as a continuous zig-zag.
// ============================================================================

interface ConstructionRowProps {
  path: string;
  definition: SlotGroupDefinition;
  index: number;
}

function ConstructionRow({ path, definition, index }: ConstructionRowProps) {
  const row = useSlotGroup(path, definition);
  const ref = useScrollReveal<HTMLDivElement>();

  // Row 1 (index 0) → light; Row 2 (index 1) → dark primary band.
  const dark = index % 2 === 1;
  // Image side alternates in lockstep: light row = image-left,
  // dark row = image-right.
  const flipped = index % 2 === 1;
  const hasCta = !!(row.url && row.label);

  const variantClass = dark ? ' bbc-row--dark' : ' bbc-row--light';
  const flipClass = flipped ? ' bbc-row--flip' : '';

  return (
    <article
      ref={ref}
      className={`reveal-fade-up bbc-row${variantClass}${flipClass}`}
    >
      {/* Angled primary band — only on dark rows. */}
      {dark ? <div className="bbc-row-band" aria-hidden="true" /> : null}

      <div className="bbc-row-inner">
        <EditableSlotGroup groupPath={path} definition={definition}>
          <div className="bbc-row-grid">
            <div className="bbc-media">
              {row.image ? (
                <VibesitesImage
                  url={row.image}
                  alt={row.alt || row.title || ''}
                  sizes="(min-width: 1024px) 45vw, 90vw"
                  className="bbc-media-img"
                />
              ) : (
                <div className="bbc-media-placeholder" />
              )}
            </div>

            <div className="bbc-copy">
              {row.title ? <h3 className="bbc-title">{row.title}</h3> : null}
              {row.body ? (
                <div
                  className="bbc-body"
                  data-vibesite-richtext=""
                  dangerouslySetInnerHTML={{ __html: row.body }}
                />
              ) : null}
              {hasCta ? (
                <a
                  className="bbc-cta"
                  href={resolveHref(row.url || '#')}
                  target={row._target || '_self'}
                >
                  <span>{row.label}</span>
                  <svg
                    aria-hidden="true"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </a>
              ) : null}
            </div>
          </div>
        </EditableSlotGroup>
      </div>
    </article>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function BetterBuiltConstruction() {
  useRevealStyles();
  const ctaColorVars = useCtaColorVars();

  const rows: { path: string; def: SlotGroupDefinition }[] = [
    { path: 'betterbuiltconstruction.row_1', def: row1Def },
    { path: 'betterbuiltconstruction.row_2', def: row2Def },
    { path: 'betterbuiltconstruction.row_3', def: row3Def },
  ];

  return (
    <section className="bbc-section" style={ctaColorVars}>
      <div className="bbc-container">
        <div className="bbc-rows">
          {rows.map((row, i) => (
            <ConstructionRow
              key={row.path}
              path={row.path}
              definition={row.def}
              index={i}
            />
          ))}
        </div>

        {/* Schema-lint anchors — hidden, never interactive. The visible rows
            above pass dynamic paths to <EditableSlotGroup>, so the source
            linter can't see each literal path. These anchors carry the
            canonical paths so `slot-group-missing-editable-wrapper` matches. */}
        <EditableSlotGroup
          groupPath="betterbuiltconstruction.row_1"
          definition={row1Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="betterbuiltconstruction.row_2"
          definition={row2Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="betterbuiltconstruction.row_3"
          definition={row3Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
      </div>

      <style>{`
        ${onColorLinksCss('.bbc-row--dark [data-vibesite-richtext]')}
        /* ── Section wrapper ── */
        /* Transparent so the light row shows the page background and the
           dark row's angled wedges reveal it at the edges. */
        .bbc-section {
          position: relative;
          background: transparent;
          padding: 0;
        }
        .bbc-container {
          position: relative;
          padding: 0;
        }
        .bbc-rows {
          display: flex;
          flex-direction: column;
        }

        /* ── Row (shared) ── */
        .bbc-row {
          position: relative;
          overflow: hidden;
          padding: 5rem 1.5rem;
        }
        @media (min-width: 768px) {
          .bbc-row { padding: 6rem 2rem; }
        }
        @media (min-width: 1024px) {
          .bbc-row { padding: 7rem 2.5rem; }
        }
        /* BetterBuiltIntro above already supplies bottom spacing. */
        .bbc-row:first-child { padding-top: 1rem; }
        @media (min-width: 768px) {
          .bbc-row:first-child { padding-top: 1.5rem; }
        }
        @media (min-width: 1024px) {
          .bbc-row:first-child { padding-top: 2rem; }
        }

        /* ── Dark variant (row 2) ── */
        /* Angled primary band — same visual language as ConvenienceCallout.
           Dark rows need extra top/bottom padding to clear the diagonal
           clip inset. */
        .bbc-row--dark {
          padding-top: 7rem;
          padding-bottom: 7rem;
        }
        @media (min-width: 768px) {
          .bbc-row--dark { padding-top: 8rem; padding-bottom: 8rem; }
        }
        @media (min-width: 1024px) {
          .bbc-row--dark { padding-top: 10rem; padding-bottom: 10rem; }
        }

        /* Tapered wedge matching the home page FloorplansList band: full
           height on the left, narrowing toward the right on both edges.
           Top and bottom insets stay equal so the content sits the same
           distance from each diagonal. */
        .bbc-row-band {
          position: absolute;
          inset: 0;
          background: var(--vibe-color-primary);
          clip-path: polygon(0 0, 100% 7%, 100% 93%, 0 100%);
          -webkit-clip-path: polygon(0 0, 100% 7%, 100% 93%, 0 100%);
        }
        /* Tablet stacks image over copy, so the row gets tall enough that a
           percentage inset eats the top padding. A fixed inset keeps a
           constant gap above the image at every tablet width. */
        @media (min-width: 768px) {
          .bbc-row-band {
            clip-path: polygon(0 0, 100% 5.5rem, 100% calc(100% - 5.5rem), 0 100%);
            -webkit-clip-path: polygon(0 0, 100% 5.5rem, 100% calc(100% - 5.5rem), 0 100%);
          }
        }
        @media (min-width: 1024px) {
          .bbc-row-band {
            clip-path: polygon(0 0, 100% 16%, 100% 84%, 0 100%);
            -webkit-clip-path: polygon(0 0, 100% 16%, 100% 84%, 0 100%);
          }
        }

        /* ── Light variant (row 1) ── */
        .bbc-row--light {
          background: #ffffff;
        }

        /* ── Inner layout ── */
        .bbc-row-inner {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
        }
        .bbc-row-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: center;
        }
        @media (min-width: 1024px) {
          .bbc-row-grid {
            grid-template-columns: 1fr 1fr;
            gap: 4rem;
          }
          /* Flipped rows place the image on the right. On mobile the image
             always sits first for consistent reading rhythm. */
          .bbc-row--flip .bbc-row-grid > .bbc-media {
            order: 2;
          }
        }

        /* ── Media ── */
        .bbc-media {
          position: relative;
          overflow: hidden;
          aspect-ratio: 4 / 3;
        }
        .bbc-row--dark .bbc-media {
          background: color-mix(in srgb, var(--vibe-color-primary) 70%, #000);
          box-shadow: 0 24px 48px -16px rgba(0, 0, 0, 0.5);
        }
        .bbc-row--light .bbc-media {
          background: var(--vibe-color-surface, #f3f4f6);
          box-shadow: 0 18px 36px -16px rgba(0, 0, 0, 0.2);
        }
        .bbc-media-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .bbc-row:hover .bbc-media-img {
          transform: scale(1.04);
        }
        .bbc-media-placeholder {
          position: absolute;
          inset: 0;
        }
        .bbc-row--dark .bbc-media-placeholder {
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--vibe-color-primary) 85%, #000) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 60%, #000) 100%
          );
        }
        .bbc-row--light .bbc-media-placeholder {
          background: linear-gradient(135deg, var(--vibe-color-surface, #e5e7eb), #f9fafb);
        }

        /* ── Copy ── */
        .bbc-copy {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 0.25rem 0;
        }

        .bbc-title {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-weight: 400;
          font-size: clamp(1.75rem, 3.2vw, 2.75rem);
          line-height: 1.08;
          letter-spacing: 0.03em;
        }
        .bbc-row--dark  .bbc-title { color: #ffffff; }
        .bbc-row--light .bbc-title { color: var(--vibe-color-secondary, #3E441D); }

        .bbc-body,
        .bbc-body * {
          font-family: var(--vibe-font-body);
        }
        .bbc-body {
          font-size: 0.9375rem;
          line-height: 1.65;
          max-width: 32rem;
        }
        @media (min-width: 768px) {
          .bbc-body { font-size: 1rem; }
        }
        .bbc-row--dark  .bbc-body { color: rgba(255, 255, 255, 0.9); }
        .bbc-row--light .bbc-body { color: rgba(17, 24, 39, 0.8); }
        .bbc-body p { margin: 0 0 0.55rem; }
        .bbc-body p:last-child { margin-bottom: 0; }
        .bbc-body ul {
          margin: 0;
          padding-left: 1.25rem;
          list-style: disc;
        }
        .bbc-body li {
          margin: 0 0 0.55rem;
          padding-left: 0.25rem;
        }
        .bbc-body li:last-child { margin-bottom: 0; }
        .bbc-body strong { font-weight: 700; }

        /* ── CTA ── */
        .bbc-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          margin-top: 0.75rem;
          padding: 1rem 2.5rem;
          background: var(--cta-bg, var(--vibe-color-secondary));
          color: var(--cta-text, var(--vibe-color-text-on-secondary, #ffffff));
          border: 1.5px solid var(--cta-bg, var(--vibe-color-secondary));
          font-family: var(--vibe-font-heading);
          font-size: 0.8125rem;
          font-weight: 400;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          text-decoration: none;
          transition: background-color 0.25s ease, color 0.25s ease,
                      transform 0.25s ease, border-color 0.25s ease;
          align-self: flex-start;
        }
        .bbc-cta svg {
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .bbc-row--dark .bbc-cta:hover {
          background: #ffffff;
          color: var(--cta-bg, var(--vibe-color-secondary));
          border-color: #ffffff;
          transform: translateY(-2px);
        }
        .bbc-row--light .bbc-cta:hover {
          background: #ffffff;
          color: var(--cta-bg, var(--vibe-color-secondary));
          border-color: var(--cta-bg, var(--vibe-color-secondary));
          transform: translateY(-2px);
        }
        .bbc-cta:hover svg {
          transform: translateX(4px);
        }

        @media (max-width: 639px) {
          .bbc-cta { width: 100%; justify-content: center; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'BetterBuiltConstruction',
  displayName: 'Better-Built Construction Rows',
  tokens: {},
  slotGroups: {
    'betterbuiltconstruction.row_1': row1Def,
    'betterbuiltconstruction.row_2': row2Def,
    'betterbuiltconstruction.row_3': row3Def,
  },
};

(BetterBuiltConstruction as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default BetterBuiltConstruction;
